/** Field/device text scale — local only (D-PS4). */

export const TEXT_SCALE_STORAGE_KEY = "galaxy.textScale";

export const TEXT_SCALE_STEPS = [
  "default",
  "medium",
  "large",
  "xlarge",
] as const;

export type TextScale = (typeof TEXT_SCALE_STEPS)[number];

export const TEXT_SCALE_LABEL: Record<TextScale, string> = {
  default: "Vừa",
  medium: "Hơi lớn",
  large: "Lớn",
  xlarge: "Rất lớn",
};

export function isTextScale(v: unknown): v is TextScale {
  return (
    typeof v === "string" &&
    (TEXT_SCALE_STEPS as readonly string[]).includes(v)
  );
}

export function readTextScale(): TextScale {
  try {
    const raw = localStorage.getItem(TEXT_SCALE_STORAGE_KEY);
    if (isTextScale(raw)) return raw;
  } catch {
    /* private mode */
  }
  return "default";
}

/** Apply to <html data-text-scale> — call before first paint when possible. */
export function applyTextScale(scale: TextScale): void {
  const root = document.documentElement;
  if (scale === "default") {
    root.removeAttribute("data-text-scale");
  } else {
    root.dataset.textScale = scale;
  }
}

export function persistTextScale(scale: TextScale): void {
  try {
    localStorage.setItem(TEXT_SCALE_STORAGE_KEY, scale);
  } catch {
    /* ignore */
  }
  applyTextScale(scale);
}

export function stepTextScale(
  current: TextScale,
  delta: -1 | 1,
): TextScale {
  const i = TEXT_SCALE_STEPS.indexOf(current);
  const next = Math.min(
    TEXT_SCALE_STEPS.length - 1,
    Math.max(0, i + delta),
  );
  return TEXT_SCALE_STEPS[next]!;
}

/** Boot: sync DOM from storage (idempotent). */
export function bootstrapTextScale(): TextScale {
  const scale = readTextScale();
  applyTextScale(scale);
  return scale;
}
